/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import React, {
    ForwardRefRenderFunction,
    KeyboardEvent,
    MouseEvent,
    Ref,
    forwardRef,
    useMemo,
} from 'react';
import styled from 'styled-components';

import { Chevron } from '@toucan/client/global/icons/animated/chevron';

import { shouldKeyDownTriggerEvent } from '@toucan/client/core/events';

import { Link } from '@toucan/client/shared/components/link';

import { styles } from './box-list-header.styles';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BoxListHeaderProps = {
    /**
     * If a loading state should be displayed
     */
    isLoading?: boolean;

    /**
     * If the heading should be a toggleable link or a static heading
     */
    isToggleable?: boolean;

    /**
     * If the component is toggled or not
     */
    isToggled?: boolean;

    /**
     * Pass up a toggle event
     */
    onToggle?: (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => void;
};

const StyledBoxListHeader = styled('header').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const BoxListHeaderWithRef: ForwardRefRenderFunction<
    HTMLDivElement,
    BoxListHeaderProps
> = (
    { isLoading, isToggleable, isToggled, onToggle }: BoxListHeaderProps,
    ref: Ref<HTMLDivElement>
) => {
    const { t } = useTranslation();

    const renderSkeleton = () => {
        return (
            <Skeleton
                variant={SkeletonVariant.TEXT}
                data-testid="product-card__box-list-header__skeleton"
            />
        );
    };

    const renderedBoxListHeader = useMemo(() => {
        if (isToggleable) {
            return (
                <div
                    className="link-wrapper"
                    data-testid="product-card___box-list-header__link-wrapper"
                    onKeyDown={(event) =>
                        shouldKeyDownTriggerEvent(event) && onToggle?.(event)
                    }
                    onClick={onToggle}
                    tabIndex={0}
                >
                    <div className="link-container">
                        <Link>{t("What's included")}</Link>
                        <Chevron className="chevron" inverted={!isToggled} />
                    </div>
                </div>
            );
        }

        return (
            <Heading
                size={HeadingSize.H5}
                className="heading"
                data-testid="product-card__box-list-header__heading"
            >
                {t("What's included")}
            </Heading>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [isToggleable, isToggled]);

    return (
        <StyledBoxListHeader ref={ref}>
            {isLoading ? renderSkeleton() : renderedBoxListHeader}
        </StyledBoxListHeader>
    );
};

export const BoxListHeader = forwardRef(BoxListHeaderWithRef);
